import React, { useEffect, useRef, useState } from 'react';
import { ArrowRightOnRectangleIcon, SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import { useAuth, SupportedLanguage } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

interface UserMenuProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({ isOpen, onClose, onLogout }) => {
  const { user, updateLanguage } = useAuth();
  const { isDarkMode, toggleDarkMode } = useTheme();
  const panelRef = useRef<HTMLDivElement>(null);
  const [languageSaving, setLanguageSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, onClose]);

  const handleLanguageChange = async (value: SupportedLanguage) => {
    setLanguageSaving(true);
    try {
      await updateLanguage(value);
    } catch (err) {
      console.error('Failed to update language preference:', err);
    } finally {
      setLanguageSaving(false);
    }
  };

  if (!isOpen) return null;

  const panelClasses = isDarkMode
    ? 'bg-slate-900/95 border-slate-700/80 text-white shadow-[0_-8px_30px_-12px_rgba(0,0,0,0.6)]'
    : 'bg-white border-gray-200 text-gray-900 shadow-[0_-8px_30px_-12px_rgba(0,0,0,0.15)]';

  const rowHover = isDarkMode ? 'hover:bg-slate-800/70' : 'hover:bg-gray-100';
  const dividerClass = isDarkMode ? 'border-slate-700/60' : 'border-gray-200';
  const labelClass = isDarkMode ? 'text-slate-400' : 'text-gray-500';

  return (
    <div
      ref={panelRef}
      role="menu"
      className={`absolute bottom-full left-2 right-2 mb-2 rounded-xl border backdrop-blur-xl overflow-hidden z-50 ${panelClasses}`}
    >
      {/* Language row */}
      <div className="px-3 pt-3 pb-2">
        <p className={`text-[10px] uppercase tracking-wide font-semibold mb-1.5 ${labelClass}`}>Reply in</p>
        <select
          value={user?.language ?? 'english'}
          disabled={languageSaving}
          onChange={(e) => handleLanguageChange(e.target.value as SupportedLanguage)}
          className={`w-full rounded-md px-2 py-1.5 text-sm border focus:outline-none focus:ring-1 focus:ring-cyan-400 ${
            isDarkMode
              ? 'bg-slate-950/60 border-slate-700 text-white'
              : 'bg-white border-gray-300 text-gray-900'
          } ${languageSaving ? 'opacity-60 cursor-wait' : ''}`}
          aria-label="Response language"
        >
          <option value="english">English</option>
          <option value="hindi">हिन्दी</option>
          <option value="gujarati">ગુજરાતી</option>
          <option value="marathi">मराठी</option>
        </select>
      </div>

      <div className={`border-t ${dividerClass}`} />

      {/* Theme row */}
      <button
        type="button"
        onClick={toggleDarkMode}
        role="menuitem"
        className={`w-full flex items-center justify-between px-3 py-2.5 text-sm transition-colors ${rowHover}`}
      >
        <span className="flex items-center gap-2">
          {isDarkMode ? <MoonIcon className="h-4 w-4" /> : <SunIcon className="h-4 w-4" />}
          <span>Theme</span>
        </span>
        <span className={`text-xs ${labelClass}`}>{isDarkMode ? 'Dark' : 'Light'}</span>
      </button>

      <div className={`border-t ${dividerClass}`} />

      {/* Logout row */}
      <button
        type="button"
        onClick={() => { onClose(); onLogout(); }}
        role="menuitem"
        className={`w-full flex items-center gap-2 px-3 py-2.5 text-sm transition-colors ${
          isDarkMode ? 'text-red-400 hover:bg-red-500/10' : 'text-red-600 hover:bg-red-50'
        }`}
      >
        <ArrowRightOnRectangleIcon className="h-4 w-4" />
        <span>Log out</span>
      </button>
    </div>
  );
};
